import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, PropsWithChildren } from 'react';
import { useMutation } from '@apollo/client';
import { MUTATION_LOGIN } from 'graphql/mutations/auth';
import useAuth from 'hooks/useAuth';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { ITenant, ITenantWithRoles } from 'types/queries';
import { UserLoginDetail, RoleSelectorMode } from 'views/pages/authentication/SelectTenant/types';
import { IRole } from 'views/backoffice/roles/types';
import { useMyTenants } from 'hooks/useMyTenants';

const ERROR_MESSAGES = {
    EMAIL_DOMAIN_NOT_ALLOWED: 'user email domain is not allowed'
};

interface TenantSelectorContextType {
    // State
    searchText: string;
    isLoading: boolean;
    hasRedirectPath: boolean;
    loadingLogin: boolean;

    // Tenant data
    tenantsWithRoles: ITenantWithRoles[];
    selectedTenant: ITenantWithRoles | null;
    selectedRole: Pick<IRole, 'name' | 'id'> | null;
    roleSelectorMode: RoleSelectorMode;
    filteredTenants: ITenantWithRoles[];

    // Actions
    setSearchText: (text: string) => void;
    setSelectedTenant: (tenant: ITenantWithRoles | null) => void;
    setSelectedRole: (role: Pick<IRole, 'name' | 'id'> | null) => void;
    setRoleSelectorMode: (selector: RoleSelectorMode) => void;
    handleLogin: (forcedTenantSelected?: Pick<ITenant, 'id' | 'name' | 'url' | 'description'>) => Promise<void>;
    handleLoginWithRedirectPath: (redirectPath: string) => Promise<void>;
    handleClose?: () => void;

    // Configuration
    switchTenantLogin?: boolean;
}

const TenantSelectorContext = createContext<TenantSelectorContextType | undefined>(undefined);

interface TenantSelectorProviderProps {
    switchTenantLogin?: boolean;
    handleClose?: () => void;
    immediate?: boolean;
}

export const TenantSelectorProvider = ({
    children,
    switchTenantLogin = false,
    handleClose,
    immediate = true
}: PropsWithChildren<TenantSelectorProviderProps>) => {
    const { loginWithBackendData } = useAuth();
    const dispatch = useDispatch();

    // State from useMyTenants
    const [selectedTenant, setSelectedTenant] = useState<ITenantWithRoles | null>(null);
    const [selectedRole, setSelectedRole] = useState<Pick<IRole, 'name' | 'id'> | null>(null);
    const [roleSelectorMode, setRoleSelectorMode] = useState<RoleSelectorMode>(RoleSelectorMode.NONE);
    const [tenantsWithRoles, setTenantsWithRoles] = useState<ITenantWithRoles[]>([]);

    const [searchText, setSearchText] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [hasRedirectPath, setHasRedirectPath] = useState(false);

    const { getUserDetails } = useMyTenants({
        immediate,
        onCompleted: async (data) => {
            const { activeTenantsWithRoles, groupedTenants } = await getTenantsWithRoles(data);
            setTenantsWithRoles(activeTenantsWithRoles);

            if (groupedTenants && Object.keys(groupedTenants).length === 1) {
                const singleTenant = Object.values(groupedTenants)[0];
                setSelectedTenant(singleTenant);
                if (singleTenant.roles.length === 1) {
                    setSelectedRole(singleTenant.roles[0]);
                } else {
                    setRoleSelectorMode(RoleSelectorMode.SINGLE);
                }
            }

            setIsLoading(false);
        }
    });

    useEffect(() => {
        getUserDetails();
    }, [getUserDetails]);

    // Logic from useMyTenants
    const getTenantsWithRoles = useCallback(async (list: UserLoginDetail[]) => {
        const groupedTenants = list.reduce((acc, item) => {
            const tenantId = item.tenant.id;

            if (!acc[tenantId]) {
                acc[tenantId] = {
                    tenant: {
                        ...item.tenant,
                        id: item.tenant.id.toString()
                    },
                    roles: []
                };
            }

            acc[tenantId].roles.push({
                ...item.role,
                id: (item as any).role.id?.toString() || Math.random().toString()
            });
            return acc;
        }, {} as Record<string, ITenantWithRoles>);

        const activeTenantsWithRoles = Object.values(groupedTenants)
            .sort((a, b) => a.tenant.name.localeCompare(b.tenant.name))
            .map((tenantGroup) => ({
                ...tenantGroup,
                roles: tenantGroup.roles.sort((a, b) => a.name.localeCompare(b.name))
            }));

        return {
            activeTenantsWithRoles,
            groupedTenants
        };
    }, []);

    const [Login, { loading: loadingLogin }] = useMutation(MUTATION_LOGIN, {
        context: {
            headers: {
                tenant_id: selectedTenant?.tenant?.id?.toString() || '',
                role_id: selectedRole?.id
            }
        },
        onCompleted: (data) => {
            localStorage.setItem('tenantSelected', 'true');
            loginWithBackendData();

            if (switchTenantLogin) {
                window.location.replace(`${localStorage.getItem('tenantPath')}/dashboard`);
                handleClose?.();
            }
        }
    });

    const filteredTenants = useMemo(() => {
        const currentTenantId = localStorage.getItem('tenant_id') || 0;
        const currentRoleId = localStorage.getItem('role_id') || 0;

        return (
            tenantsWithRoles
                .map(({ tenant, roles }) => {
                    if (tenant.id === currentTenantId.toString()) {
                        return {
                            tenant,
                            roles: switchTenantLogin ? roles.filter((role) => role.id !== currentRoleId.toString()) : roles
                        };
                    }
                    return {
                        tenant,
                        roles
                    };
                })
                .filter(
                    ({ tenant }) =>
                        tenant.name.toLowerCase().includes(searchText.toLowerCase()) ||
                        tenant.url.toLowerCase().includes(searchText.toLowerCase())
                ) || []
        );
    }, [tenantsWithRoles, searchText, switchTenantLogin]);

    const handleLogin = useCallback(
        async (forcedTenantSelected?: Pick<ITenant, 'id' | 'name' | 'url' | 'description'>) => {
            if (!selectedTenant && !forcedTenantSelected) return;
            const currentTenant = (selectedTenant as ITenantWithRoles) || forcedTenantSelected;

            localStorage.setItem('tenant_id', currentTenant?.tenant?.id?.toString() as string);
            localStorage.setItem('tenantPath', `/${currentTenant?.tenant?.url}`);
            localStorage.setItem('role_id', selectedRole?.id?.toString() as string);

            try {
                await Login();
            } catch (err: any) {
                if (err.message?.toLowerCase().startsWith(ERROR_MESSAGES.EMAIL_DOMAIN_NOT_ALLOWED)) {
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: err.message,
                            variant: 'alert',
                            alert: { color: 'error' }
                        })
                    );
                } else {
                    console.log('err', err);
                }
            }
        },
        [selectedTenant, Login, dispatch, selectedRole]
    );

    const handleLoginWithRedirectPath = useCallback(
        async (redirectPath: string) => {
            const tenant = tenantsWithRoles.find((tenantItem: ITenantWithRoles) => tenantItem.tenant.url === redirectPath.split('/')[1]);
            if (!tenant) {
                setHasRedirectPath(false);
                return;
            }
            localStorage.setItem('tenant_id', tenant.tenant?.id.toString() as string);
            localStorage.setItem('tenantPath', `/${tenant.tenant?.url}`);
            localStorage.setItem('role_id', selectedRole?.id?.toString() as string);
            try {
                await Login();
            } catch (err: any) {
                console.log('err', err);
            }
            setHasRedirectPath(false);
        },
        [Login, tenantsWithRoles, selectedRole]
    );

    useEffect(() => {
        const redirectPath = localStorage.getItem('redirectPath');
        if (redirectPath) {
            setHasRedirectPath(true);

            if (selectedRole) {
                handleLoginWithRedirectPath(redirectPath);
            }
        }
    }, [handleLoginWithRedirectPath, selectedRole]);

    useEffect(() => {
        const currentTenantId = localStorage.getItem('tenant_id') || 0;
        if (currentTenantId && switchTenantLogin) {
            setSelectedTenant(tenantsWithRoles.find((tenant) => tenant.tenant.id === currentTenantId) || null);
        }
    }, [tenantsWithRoles, switchTenantLogin]);

    const contextValue: TenantSelectorContextType = {
        // State
        searchText,
        isLoading,
        hasRedirectPath,
        loadingLogin,

        // Tenant data
        tenantsWithRoles,
        selectedTenant,
        selectedRole,
        roleSelectorMode,
        filteredTenants,

        // Actions
        setSearchText,
        setSelectedTenant,
        setSelectedRole,
        setRoleSelectorMode,
        handleLogin,
        handleLoginWithRedirectPath,
        handleClose,

        // Configuration
        switchTenantLogin
    };

    return <TenantSelectorContext.Provider value={contextValue}>{children}</TenantSelectorContext.Provider>;
};

export const useTenantSelector = (): TenantSelectorContextType => {
    const context = useContext(TenantSelectorContext);
    if (context === undefined) {
        throw new Error('useTenantSelector must be used within a TenantSelectorProvider');
    }
    return context;
};

export default TenantSelectorContext;
